import { Engine } from "@babylonjs/core/Engines/engine";
import "@babylonjs/core/Loading/loadingScreen";
import "@babylonjs/loaders/glTF";

import { SceneManager } from "./SceneManager";
import { MainMenuScene } from "../scenes/MainMenuScene";
import { BuilderScene } from "../scenes/BuilderScene";
import { FlightScene } from "../scenes/FlightScene";
import { SaveManager } from "../save/SaveManager";
import { Achievements } from "../meta/Achievements";
import { showToast } from "../ui/Toast";

export class Game {
  readonly engine: Engine;
  readonly sceneManager: SceneManager;
  readonly saveManager: SaveManager;
  readonly achievements: Achievements;

  constructor(readonly canvas: HTMLCanvasElement, readonly uiRoot: HTMLElement) {
    this.engine = new Engine(canvas, true, {
      antialias: true,
      preserveDrawingBuffer: false,
      stencil: true,
      adaptToDeviceRatio: true,
      powerPreference: "high-performance",
    });
    // Cap DPR to 2.0 — iPad Pro is 2x natively, and cranking higher wastes GPU.
    this.engine.setHardwareScalingLevel(1 / Math.min(window.devicePixelRatio || 1, 2));

    this.saveManager = new SaveManager();
    this.achievements = new Achievements(this.saveManager, (a) =>
      showToast(uiRoot, `Achievement: ${a.title}`, "trophy"),
    );

    this.sceneManager = new SceneManager(this);

    window.addEventListener("resize", this.onResize);
  }

  async start() {
    await this.saveManager.ready();
    await this.achievements.load();

    this.sceneManager.register("menu", () => new MainMenuScene(this));
    this.sceneManager.register("builder", (arg) => new BuilderScene(this, arg));
    this.sceneManager.register("flight", (arg) => new FlightScene(this, arg));

    await this.sceneManager.go("menu");

    this.engine.runRenderLoop(() => {
      const scene = this.sceneManager.activeBabylonScene;
      if (scene) scene.render();
    });
  }

  private onResize = () => {
    this.engine.resize();
  };

  dispose() {
    window.removeEventListener("resize", this.onResize);
    this.sceneManager.dispose();
    this.engine.dispose();
  }
}
